import { Component, OnInit, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { RippleModule } from 'primeng/ripple';

/**
 * Composant de changement de langue avec modale
 * Affiche la langue actuelle et permet de changer via une modale
 * 
 * Styles: src/scss/components/_language-switcher.scss (architecture 7-1)
 */
@Component({
  selector: 'app-language-switcher',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    RippleModule,
  ],
  templateUrl: './language-switcher.component.html',
})
export class LanguageSwitcherComponent implements OnInit {
  private translateService = inject(TranslateService);

  // État de la modale
  showLanguageDialog = false;

  // Langue courante
  currentLanguage = 'fr';

  // Langues disponibles
  languages = [
    {
      code: 'fr',
      name: 'Français',
      flag: '🇫🇷',
      nativeName: 'Français',
    },
    {
      code: 'en',
      name: 'English',
      flag: '🇬🇧',
      nativeName: 'English',
    },
  ];

  ngOnInit(): void {
    // Récupérer la langue courante
    this.currentLanguage = this.translateService.currentLang || 'fr';

    // Écouter les changements de langue
    this.translateService.onLangChange.subscribe((event) => {
      this.currentLanguage = event.lang;
    });
  }

  /**
   * Obtenir le libellé court de la langue (FR, EN)
   */
  get currentLanguageLabel(): string {
    return this.currentLanguage.toUpperCase();
  }

  /**
   * Obtenir l'objet de la langue courante
   */
  get currentLanguageObject() {
    return this.languages.find((lang) => lang.code === this.currentLanguage);
  }

  /**
   * Ouvrir la modale de sélection de langue
   */
  openLanguageDialog(): void {
    this.showLanguageDialog = true;
  }

  /**
   * Fermer la modale
   */
  closeLanguageDialog(): void {
    this.showLanguageDialog = false;
  }

  /**
   * Changer de langue
   */
  changeLanguage(languageCode: string): void {
    this.translateService.use(languageCode);
    localStorage.setItem('preferredLanguage', languageCode);
    this.currentLanguage = languageCode;
    this.closeLanguageDialog();

    // Annoncer le changement pour l'accessibilité
    this.announceLanguageChange(languageCode);
  }

  /**
   * Vérifier si une langue est active
   */
  isActiveLanguage(languageCode: string): boolean {
    return this.currentLanguage === languageCode;
  }

  /**
   * Annoncer le changement de langue pour les lecteurs d'écran
   */
  private announceLanguageChange(languageCode: string): void {
    const languageName = this.languages.find((lang) => lang.code === languageCode)?.name;
    if (languageName) {
      const message = `Language changed to ${languageName}`;
      
      // Créer un élément invisible pour l'annonce ARIA
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = message;
      
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  }
}

